import React, { useState, useEffect } from 'react';
import {
    FiX,
    FiCheck,
    FiZap,
    FiShield,

} from 'react-icons/fi';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { FaCopy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import apiUtils from 'utils/apiutils';
import { useCooldownAction } from 'hooks/useCooldownAction';
import { removeUnderscore } from 'helpers/removeUnderscore';
import SubscriptionTable from 'components/SubscriptionTable';

const Feature = ({ label, value }) => (
    <div className="flex items-center gap-3 text-white">
        <div className="p-1 rounded-full bg-green-500/20 text-green-400">
            <FiCheck className="w-3 h-3" />
        </div>
        {label}: {value}
    </div>
);

const PlanCard = ({ plan, onBuy, isPopular }) => {
    return (
        <div
            className={`relative overflow-hidden rounded-3xl p-8 transition-all duration-500 hover:scale-105 group 
          ${isPopular
                    ? "bg-white/5 border-2 border-primary/50 shadow-neon"
                    : "bg-white/5 border border-white/10 backdrop-blur-xl"}
        `}
        >
            {/* Header */}
            <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                    {removeUnderscore(plan.Name)}
                </h3>
                <p className="text-sm text-gray-400 flex items-center justify-center gap-1">
                    <FiShield className="w-4 h-4" />
                    {plan.Package_Type} Package
                </p>
            </div>

            {/* Price & Validity */}
            <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold bg-clip-text text-transparent bg-primary">
                        ${plan.Price}
                    </span>
                    <span className="text-gray-400 text-lg">/ {plan.Validity} days</span>
                </div>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-8 text-sm">
                <Feature label="Live API" value={plan.Live_API} />
                <Feature label="Paper API" value={plan.Paper_API} />
                <Feature label="Live Bots" value={plan.Live_BOT} />
                <Feature label="Paper Bots" value={plan.Paper_BOT} />

                {/* Optional: Show total backtests */}
                {plan.BackTest_Total && (
                    <Feature
                        label="Backtests (TP/SL & Liquidation)"
                        value={`${plan.BackTest_Total["TP_SL"] + plan.BackTest_Total["LIQUIDATION"]}`}
                    />
                )}
            </div>

            {/* Buy Button */}
            <button
                onClick={() => onBuy(plan)}
                className={`w-full h-12 px-6 flex items-center justify-center gap-2 rounded-2xl font-semibold text-lg transition-all duration-300
            ${isPopular
                        ? "bg-primary text-black hover:shadow-[0_0_15px_rgba(255,255,255,0.3)] hover:scale-105"
                        : "bg-secondary text-white hover:shadow-[0_0_12px_rgba(0,255,255,0.4)] hover:scale-105"
                    }`}
            >
                <span>Get Started</span>
                <FiZap className="w-5 h-5" />
            </button>
        </div>
    );
};

const ConfirmationModal = ({ isOpen, onClose, plan, onConfirm }) => {
    if (!isOpen) return null;
    const runWithCooldown = useCooldownAction(); // 10 sec cooldown

    const handleConfirmClick = () => {
        runWithCooldown(() => onConfirm(plan, plan.Name), "10 seconds have not passed since your last purchase attempt.");
    };
    const [accepted, setAccepted] = useState(false);


    return (
        <div className="fixed inset-0 min-h-screen text-white bg-black bg-opacity-75 flex items-center justify-center z animate-fade-in">

            {/* <div className="absolute inset-0  backdrop-blur-sm" onClick={onClose} /> */}

            <div className="bg-secondary rounded-xl p-8 max-w-lg w-full  mx-auto">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                    <FiX className="w-5 h-5" />
                </button>

                <div className="text-center mb-6">

                    <h2 className="text-2xl font-bold mb-2">Confirm Subscription</h2>
                    <p className="text-gray-400">
                        You're about to upgrade to the{' '}
                        <span className="text-accent font-semibold">{plan?.Name}</span> plan
                    </p>
                </div>

                <div className="bg-white/5 rounded-2xl p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-400">Plan</span>
                        <span className="font-semibold">{removeUnderscore(plan?.Name)}</span>
                    </div>

                    <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-400">Duration</span>
                        <span className="font-semibold">{plan?.Validity} days</span>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                        <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold">Total</span>
                            <span className="text-2xl font-bold text-accent">${plan?.Price}</span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-4 mb-2">
                    {/* Terms & Conditions checkbox */}
                    <label className="flex items-center space-x-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={accepted}
                            onChange={(e) => setAccepted(e.target.checked)}
                            className="w-4 h-4 accent-blue-500 rounded"
                        />
                        <span className="text-gray-400">
                            I agree to upgrade to the{' '}
                            <span className="text-accent text-white font-semibold">{removeUnderscore(plan?.Name)}</span> plan

                        </span>

                    </label>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        variant="outline"
                        className="flex-1 bg-white bg-opacity-10 text-white lg:py-3 lg:px-6 rounded-lg font-semibold hover:bg-opacity-20 transition-colors"

                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirmClick}
                        disabled={!accepted}
                        className={`flex-1 h-12 bg-primary text-secondary rounded-lg lg:py-3 lg:px-6 transition 
          ${accepted
                                ? "cursor-pointer hover:shadow-neon"
                                : " cursor-not-allowed"
                            }`}
                    >
                        Confirm Purchase
                    </button>
                </div>
            </div>
        </div>
    );
};

const MainSubscription = () => {
    const [subsdata, setSubsdata] = useState([]);
    const [activeTab, setActiveTab] = useState('Monthly');
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [QRCodeData, setQRCodeData] = useState(null);
    const [addfundloading, setaddfundloading] = useState(false)
    //   const {fetchlockprod} = useContext(DataContext)
    const navigate = useNavigate()

    const handleAddFund = async (monthprice, yearprice) => {
        setShowModal(false);
        setaddfundloading(true)
        const postdata = {
            amount_usdt: monthprice || yearprice,
            currency: 'USDT.BEP20',
            gateway: 'PROFITSLA',
        };

        try {
            const response = await apiUtils.post('/deposit/make_payment', postdata, { showToast: false });
            if (response.Success) {
                setIsModalVisible(true);
                setShowModal(false);
                setaddfundloading(false)
                setQRCodeData(response.Success);

            }
        } catch (error) {
            setaddfundloading(false)
        }
    };

    const getSubsdata = async () => {
        try {
            setLoading(true);
            const response = await apiUtils.post('/subscription/view_subscription_package', { showToast: false });
            if (response?.Success) setSubsdata(response.Success);
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    const handleBuySubs = async (planName, plan) => {
        try {
            const postdata = {
                name: planName,

            };

            const response = await apiUtils.post('/subscription/buy_subscription', postdata, { showToast: false });
            if (response?.Success) {
                toast.success(response?.Success)
                // fetchlockprod()
                setShowModal(false);
                //  if (planName === 'BOT_0') {
                // handleAddFund(plan.Price, plan.Price)
            // }
            }
        } catch (err) {
            // console.log(err.response.data.Error,"err")
           
            if (err.response.data.Error === "You do not have enough balance") {
                toast.error(err.response.data.Error)
            }
        }
    };

    useEffect(() => {
        getSubsdata();
    }, []);

    const handleBuyClick = (plan) => {
        setSelectedPlan(plan);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
    };



    if (addfundloading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
            </div>
        );
    }

    return (
        <>
        <div className="min-h-screen  text-white overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
            <div className="relative px-4 py-4 lg:py-2">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-1">
                        <h1 className="text-5xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-white to-gray-300 bg-clip-text text-transparent">
                            Get the Complete
                            <br />
                            <span className="bg-primary bg-clip-text text-transparent">
                                Profitsla Experience
                            </span>
                        </h1>

                        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-12">
                            Unlock powerful features and take your trading to the next level with our premium subscription plans
                        </p>

                        {/* Tab Switcher */}
                        {/* <div className="inline-flex bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-2 mb-16">
              {['Monthly', 'Yearly'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === tab
                    ? 'bg-primary text-secondary shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  {tab}
                  {tab === 'Yearly' && (
                    <span className="ml-2 text-xs bg-secondary text-white px-2 py-1 rounded-full">
                      Save 20%
                    </span>
                  )}
                </button>
              ))}
            </div> */}
                    </div>

                    {/* Plans Grid */}
                    {loading ? (
                        <>



                        </>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 justify-center">
                            {subsdata.map((plan, index) => (
                                <PlanCard
                                    key={index}
                                    plan={plan}
                                    variantKey={activeTab}
                                    onBuy={handleBuyClick}
                                    isPopular={plan.Name === 'BOT_500'} // mark premium plan

                                    activeTab={activeTab}
                                />
                            ))}
                        </div>
                    )}


                </div>
            </div>

            <ConfirmationModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                plan={selectedPlan}
                onConfirm={async (plan) => {
                    await handleBuySubs(plan.Name, plan);
                }}
            />

            {isModalVisible && (
                <div className="fixed inset-0 min-h-screen text-white bg-dark-900  bg-opacity-75 flex items-center justify-center z-[100] animate-fade-in">
                    <div className="bg-secondary rounded-xl p-4 max-w-lg w-full  mx-auto">
                        <div className="bg--900 rounded-xl overflow-hidden">
                            {/* Header */}
                            <div className="p-4 border-b border-gray-700">
                                <h2 className="text-2xl font-bold text-white text-center mb-2">Scan QR Code</h2>
                                <div className="text-sm text-gray-300 space-y-1 mb-4 text-center">
                                    <p>1. Scan the QR code using your wallet app.</p>
                                    <p>
                                        2. Send exactly{' '}
                                        <span className="font-semibold text-primary">
                                            ${QRCodeData?.Amount?.toFixed(4)} USDT.BEP20
                                        </span>
                                        .
                                    </p>
                                    <p>
                                        3. Wait for confirmation on the{' '}
                                        <span className="font-semibold text-primary"> USDT.BEP20</span> network.
                                    </p>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-2">
                                {/* Right Column - QR + Address */}
                                <div>
                                    <div className="flex justify-center mb-4">
                                        <QRCodeCanvas
                                            value={QRCodeData?.QR_Code}
                                            size={160}
                                            className="border border-gray-700 rounded-lg"
                                            fgColor="#0f172a"
                                            bgColor="#fae8ff"
                                        />
                                    </div>

                                </div>
                                {/* Left Column */}
                                <div className="flex flex-col items-center text-center">
                                    <div className="text-lg font-bold text-primary mb-2">
                                        {QRCodeData?.Amount?.toFixed(4)}  USDT.BEP20
                                    </div>
                                    <div className="text-sm text-red-500 mb-4">
                                        ⚠️ Only deposit  USDT.BEP20. Sending other assets will result in loss.
                                    </div>
                                    <div className="text-xs text-gray-300 space-y-1">
                                        <p>✅ Use a regular send, not from a smart contract.</p>
                                        <p>✅ Send enough to cover gas/fees.</p>
                                    </div>
                                </div>


                            </div>

                            <div className='flex flex-col  items-center'>
                                <p className="text-sm text-white mb-1">Send to the wallet address below:</p>
                                <div className="text-xs text-white mb-2">
                                    Transaction ID: <span className="font-semibold">{QRCodeData?.TransID}</span>
                                </div>
                                <div className="flex items-center gap-2 w-full">
                                    <input
                                        type="text"
                                        value={QRCodeData?.Address || ''}
                                        readOnly
                                        className="flex-1 px-3 py-2 text-xs bg-gray-800 text-gray-300 border border-gray-700 rounded-md"
                                    />
                                    <button
                                        onClick={() => {
                                            navigator.clipboard.writeText(QRCodeData?.Address);
                                            toast.success('Wallet address copied!');
                                        }}
                                        className="cursor-pointer transition"
                                    >
                                        <FaCopy />
                                    </button>
                                </div>


                            </div>

                            {/* Footer Buttons */}
                            <div className="flex gap-4 p-4 border-t border-gray-700">
                                <button
                                    onClick={handleModalClose}
                                    className="w-full py-2 text-xs bg-gray-700 text-white rounded-md hover:bg-gray-600 transition"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => navigate('/dashboard/transactions?tab=subscription')}
                                    className="w-full py-2 text-xs bg-primary text-white rounded-md hover:opacity-90 transition"
                                >
                                    Confirm Payment
                                </button>
                            </div>

                            {/* Help Section */}
                            <div className="p-4 border-t border-gray-700">
                                <details className="bg-gray-800 p-3 rounded-md">
                                    <summary className="text-sm text-white font-semibold cursor-pointer">Need Help?</summary>
                                    <div className="mt-2 text-xs text-gray-300 space-y-1">
                                        <p>• Use a  USDT.BEP20 compatible wallet.</p>
                                        <p>• Verify amount and address before sending.</p>
                                        <p>• Contact support if transaction fails.</p>
                                    </div>
                                </details>
                            </div>
                        </div>
                    </div>
                </div>

            )}
        </div>
        <div className='container mx-auto px-4 py-8'>
        <SubscriptionTable />
        </div>
        </>
    );
};

export default MainSubscription